import { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Loader2, Plug, Key, Plus, Trash2, Copy, Check, Upload, FileSpreadsheet,
  ShoppingCart, UtensilsCrossed, AlertCircle, Code, RefreshCw, Download,
  Sparkles, Package, X, ChevronRight, Search, Zap, Globe, Mail, Clock,
  ArrowRight, Star, Shield, Wifi, Database, Settings, Bell, CheckCircle2,
  Activity, TrendingUp, CreditCard, Banknote, Smartphone, BarChart3, Radio,
  Play, ChevronDown, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { CSVImportCard } from "../components/tpv/CSVImportCard";
import { WebhookInfoCard } from "../components/tpv/WebhookInfoCard";

// ─── Constants ─────────────────────────────────────────────────────

const FIELD_LABELS = {
  sales: {
    date: "Fecha", product_name: "Producto", quantity: "Cantidad",
    unit_price: "Precio unitario", total: "Total", payment_method: "Pago",
  },
  menu: { name: "Nombre", description: "Descripción", price: "Precio", category: "Categoría" },
  inventory: {
    name: "Ingrediente", quantity: "Stock actual", unit: "Unidad",
    cost_per_unit: "Coste/ud.", min_stock: "Stock mín.", category: "Categoría",
  },
  expenses: {
    date: "Fecha", supplier_name: "Proveedor", supplier_tax_id: "NIF/CIF",
    invoice_number: "Nº Factura", notes: "Concepto", category: "Categoría",
    base_amount: "Base", tax_amount: "IVA", total_amount: "Total",
    payment_method: "Pago",
  },
};

const TYPE_META = {
  sales: { label: "Ventas", icon: ShoppingCart, color: "text-green-600", bg: "bg-green-50 dark:bg-green-950/20" },
  menu: { label: "Menú", icon: UtensilsCrossed, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950/20" },
  inventory: { label: "Inventario", icon: Package, color: "text-amber-700", bg: "bg-amber-50 dark:bg-amber-950/20" },
  expenses: { label: "Gastos", icon: Receipt, color: "text-rose-700", bg: "bg-rose-50 dark:bg-rose-950/20" },
};

const INTEGRATION_TYPE_META = {
  webhook: {
    label: "Webhook (Tiempo Real)",
    icon: Zap,
    color: "text-green-600",
    bg: "bg-green-50 dark:bg-green-950/30",
    border: "border-green-200 dark:border-green-800",
    badge: "bg-green-100 text-green-800",
    desc: "Tu TPV notifica automáticamente a MenuGenius tras cada venta. Sincronización instantánea, sin intervención manual.",
  },
  api: {
    label: "Conexión API",
    icon: Globe,
    color: "text-blue-600",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    border: "border-blue-200 dark:border-blue-800",
    badge: "bg-blue-100 text-blue-800",
    desc: "Nos conectamos a la API de tu TPV para sincronizar datos periódicamente. Fiable y automático.",
  },
  csv: {
    label: "Importación de Ficheros",
    icon: FileSpreadsheet,
    color: "text-amber-700",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800",
    badge: "bg-amber-100 text-amber-800",
    desc: "Exporta un CSV o Excel desde tu TPV y súbelo manualmente. Compatible con cualquier TPV del mercado.",
  },
};

const CONFIDENCE_LABELS = {
  alta: { label: "Alta confianza", color: "text-green-600", bg: "bg-green-100" },
  media: { label: "Confianza media", color: "text-amber-700", bg: "bg-amber-100" },
  baja: { label: "Confianza baja", color: "text-muted-foreground", bg: "bg-muted" },
};

// ─── Country code detection from browser ────────────────────────────
function detectCountryCode() {
  try {
    const lang = navigator.language || navigator.languages?.[0] || "";
    const parts = lang.split("-");
    if (parts.length >= 2) return parts[parts.length - 1].toUpperCase();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Madrid") || tz.includes("Canary")) return "ES";
    if (tz.includes("Mexico")) return "MX";
    if (tz.includes("Buenos_Aires") || tz.includes("Argentina")) return "AR";
    if (tz.includes("Bogota")) return "CO";
    if (tz.includes("Santiago")) return "CL";
    if (tz.includes("London")) return "GB";
    if (tz.includes("Paris")) return "FR";
    if (tz.includes("Berlin") || tz.includes("Vienna")) return "DE";
    if (tz.includes("New_York") || tz.includes("Chicago") || tz.includes("Los_Angeles")) return "US";
  } catch {}
  return null;
}

// ─── Main Component ──────────────────────────────────────────────────

export default function TPVIntegrationPage() {
  const { currentRestaurant } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiDocs, setApiDocs] = useState(null);
  const [activeTab, setActiveTab] = useState("configuracion");

  // POS Config state
  const [posSystems, setPosSystems] = useState([]);
  const [posConfig, setPosConfig] = useState(null);
  const [posSearch, setPosSearch] = useState("");
  const [selectedPOS, setSelectedPOS] = useState(null);
  const [customPOSName, setCustomPOSName] = useState("");
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);
  const [chosenIntegration, setChosenIntegration] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [countryCode] = useState(detectCountryCode);

  // Reminder state
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderFrequency, setReminderFrequency] = useState("weekly");
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);

  // API key dialog
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyPermissions, setNewKeyPermissions] = useState({
    sales_write: true, sales_read: true, menu_write: false, menu_read: false,
  });
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // CSV import
  const salesFileRef = useRef(null);
  const menuFileRef = useRef(null);
  const inventoryFileRef = useRef(null);
  const expensesFileRef = useRef(null);
  const [analyzingCSV, setAnalyzingCSV] = useState(false);
  const [importing, setImporting] = useState(false);

  // Bulk import (historical, via API key)
  const bulkFileRef = useRef(null);
  const [bulkFile, setBulkFile]           = useState(null);
  const [bulkApiKey, setBulkApiKey]       = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult]       = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [editedMapping, setEditedMapping] = useState({});

  // Live Feed state
  const [liveFeedData, setLiveFeedData] = useState(null);
  const [liveFeedLoading, setLiveFeedLoading] = useState(false);
  const [liveRefreshCountdown, setLiveRefreshCountdown] = useState(10);
  const [liveIsPolling, setLiveIsPolling] = useState(false);
  const [sendingTestEvent, setSendingTestEvent] = useState(false);
  const [newSaleIds, setNewSaleIds] = useState(new Set());
  const liveIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const liveFeedDataRef = useRef(null); // keeps latest data to avoid stale closure

  // ─── Main Data Fetching ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const [statusData, keysData, docsData, systemsData, configData] = await Promise.all([
        api.getTPVStatus(currentRestaurant.restaurant_id),
        api.getTPVApiKeys(currentRestaurant.restaurant_id),
        api.getTPVDocs(),
        api.getPOSSystems(countryCode),
        api.getPOSConfig(currentRestaurant.restaurant_id),
      ]);
      setStatus(statusData);
      setApiKeys(keysData);
      setApiDocs(docsData);
      setPosSystems(systemsData.systems || []);
      if (configData && configData.pos_system_id) {
        setPosConfig(configData);
        const sys = systemsData.systems?.find(s => s.id === configData.pos_system_id);
        if (sys) setSelectedPOS(sys);
        if (configData.pos_system_id === "other") setCustomPOSName(configData.pos_system_name || "");
        setChosenIntegration(configData.integration_type);
        if (configData.reminder_email) setReminderEmail(configData.reminder_email);
        if (configData.reminder_frequency) setReminderFrequency(configData.reminder_frequency);
        if (configData.reminder_enabled !== undefined) setReminderEnabled(configData.reminder_enabled);
        if (configData.ai_recommendation) {
          try { setAiRecommendation(JSON.parse(configData.ai_recommendation)); } catch {}
        }
      }
    } catch (error) {
      console.error("Failed to fetch TPV data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant, countryCode]);

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchData();
  }, [currentRestaurant, fetchData]);

  // Live feed polling: moved after startPolling/stopPolling definitions

  const fetchLiveFeed = useCallback(async (isInitial = false) => {
    if (!currentRestaurant) return;
    if (isInitial) setLiveFeedLoading(true);
    try {
      const prevIds = new Set((liveFeedDataRef.current?.recent_sales || []).map(s => s.sale_id));
      const data = await api.getTPVLiveFeed(currentRestaurant.restaurant_id);
      liveFeedDataRef.current = data;
      setLiveFeedData(data);
      // Mark new entries for animation
      if (!isInitial) {
        const newIds = new Set(data.recent_sales.filter(s => !prevIds.has(s.sale_id)).map(s => s.sale_id));
        if (newIds.size > 0) {
          setNewSaleIds(newIds);
          setTimeout(() => setNewSaleIds(new Set()), 3000);
        }
      }
    } catch (err) {
      console.error("Live feed error:", err);
    } finally {
      if (isInitial) setLiveFeedLoading(false);
    }
  }, [currentRestaurant]);

  const startPolling = useCallback(() => {
    // Clear any stale intervals first to prevent accumulation
    if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setLiveIsPolling(true);
    setLiveRefreshCountdown(10);
    liveIntervalRef.current = setInterval(() => {
      fetchLiveFeed(false);
      setLiveRefreshCountdown(10);
    }, 10000);
    countdownIntervalRef.current = setInterval(() => {
      setLiveRefreshCountdown(prev => (prev > 0 ? prev - 1 : 10));
    }, 1000);
  }, [fetchLiveFeed]);

  const stopPolling = useCallback(() => {
    setLiveIsPolling(false);
    if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  // Live feed polling: start when tab is active, stop otherwise
  useEffect(() => {
    if (activeTab === "live" && currentRestaurant) {
      fetchLiveFeed(true);
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [activeTab, currentRestaurant, fetchLiveFeed, startPolling, stopPolling]);

  const handleSendTestEvent = async () => {
    setSendingTestEvent(true);
    try {
      const result = await api.sendTPVTestEvent(currentRestaurant.restaurant_id);
      toast.success(result.message || "Evento de prueba enviado");
      await fetchLiveFeed(false);
    } catch {
      toast.error("Error al enviar evento de prueba");
    } finally {
      setSendingTestEvent(false);
    }
  };

  // ── POS Selection & AI Recommendation ──────────────────────────────

  const handleSelectPOS = (pos) => {
    setSelectedPOS(pos);
    setAiRecommendation(null);
    setChosenIntegration(null);
    if (pos.id !== "other") setCustomPOSName("");
  };

  const handleGetAIRecommendation = async () => {
    const posName = selectedPOS?.id === "other" ? customPOSName : selectedPOS?.name;
    if (!posName) return;
    setLoadingRecommendation(true);
    setAiRecommendation(null);
    try {
      const result = await api.aiPOSRecommend(posName, currentRestaurant?.business_type || "restaurante");
      setAiRecommendation(result);
      if (result.recommended_type && !chosenIntegration) {
        setChosenIntegration(result.recommended_type);
      }
    } catch {
      toast.error("Error al obtener recomendación de IA");
    } finally {
      setLoadingRecommendation(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedPOS || !chosenIntegration) {
      toast.error("Selecciona un TPV y un método de integración");
      return;
    }
    const posName = selectedPOS.id === "other" ? customPOSName : selectedPOS.name;
    if (!posName) { toast.error("Introduce el nombre de tu TPV"); return; }
    setSavingConfig(true);
    try {
      await api.savePOSConfig(currentRestaurant.restaurant_id, {
        pos_system_id: selectedPOS.id,
        pos_system_name: posName,
        integration_type: chosenIntegration,
        ai_recommendation: aiRecommendation ? JSON.stringify(aiRecommendation) : null,
        reminder_email: reminderEmail || null,
        reminder_frequency: reminderFrequency,
        reminder_enabled: reminderEnabled,
      });
      toast.success("Configuración guardada correctamente");
      fetchData();
    } catch { toast.error("Error al guardar la configuración"); }
    finally { setSavingConfig(false); }
  };

  // ── Reminders ──────────────────────────────────────────────────────

  const handleSaveReminder = async () => {
    if (!reminderEmail) { toast.error("Introduce un email para los recordatorios"); return; }
    setSavingReminder(true);
    try {
      await api.schedulePOSReminder(currentRestaurant.restaurant_id, reminderEmail, reminderFrequency, reminderEnabled);
      toast.success(reminderEnabled ? "Recordatorio activado. Recibirás un email de confirmación." : "Recordatorio desactivado");
      fetchData();
    } catch { toast.error("Error al configurar el recordatorio"); }
    finally { setSavingReminder(false); }
  };

  // ── API Keys ──────────────────────────────────────────────────────

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) { toast.error("Introduce un nombre para la API key"); return; }
    const permissions = Object.entries(newKeyPermissions).filter(([, v]) => v).map(([k]) => k).join(",");
    if (!permissions) { toast.error("Selecciona al menos un permiso"); return; }
    setCreatingKey(true);
    try {
      const result = await api.createTPVApiKey(currentRestaurant.restaurant_id, newKeyName, permissions);
      setNewKeyResult(result);
      fetchData();
      toast.success("API key creada correctamente");
    } catch { toast.error("Error al crear API key"); }
    finally { setCreatingKey(false); }
  };

  const handleDeleteKey = async (keyId) => {
    if (!confirm("¿Seguro que quieres desactivar esta API key?")) return;
    try {
      await api.deleteTPVApiKey(currentRestaurant.restaurant_id, keyId);
      fetchData();
      toast.success("API key desactivada");
    } catch { toast.error("Error al desactivar API key"); }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
    toast.success("Copiado al portapapeles");
  };

  // ── CSV Import ────────────────────────────────────────────────────

  const handleCSVSelected = async (file, type) => {
    if (!file) return;
    setAnalyzingCSV(true);
    setPreviewData(null);
    setPreviewType(type);
    setPreviewFile(file);
    try {
      let result;
      if (type === "sales") result = await api.importSalesCSV(currentRestaurant.restaurant_id, file, true);
      else if (type === "menu") result = await api.importMenuCSV(currentRestaurant.restaurant_id, file, true);
      else if (type === "expenses") result = await api.importExpensesCSV(currentRestaurant.restaurant_id, file, true);
      else result = await api.importInventoryCSV(currentRestaurant.restaurant_id, file, true);
      setPreviewData(result);
      setEditedMapping(result.column_mapping || {});
    } catch (err) {
      toast.error(err.message || "Error al analizar el CSV");
      setPreviewType(null);
      setPreviewFile(null);
    } finally {
      setAnalyzingCSV(false);
      if (salesFileRef.current) salesFileRef.current.value = "";
      if (menuFileRef.current) menuFileRef.current.value = "";
      if (inventoryFileRef.current) inventoryFileRef.current.value = "";
      if (expensesFileRef.current) expensesFileRef.current.value = "";
    }
  };

  const handleConfirmImport = async () => {
    if (!previewFile || !previewType) return;
    setImporting(true);
    const colMapping = Object.keys(editedMapping).length > 0 ? editedMapping : (previewData?.column_mapping || null);
    try {
      let result;
      if (previewType === "sales") result = await api.importSalesCSV(currentRestaurant.restaurant_id, previewFile, false, colMapping);
      else if (previewType === "menu") result = await api.importMenuCSV(currentRestaurant.restaurant_id, previewFile, false, colMapping);
      else if (previewType === "expenses") result = await api.importExpensesCSV(currentRestaurant.restaurant_id, previewFile, false, colMapping);
      else result = await api.importInventoryCSV(currentRestaurant.restaurant_id, previewFile, false, colMapping);
      fetchData();
      setPreviewData(null); setPreviewFile(null); setPreviewType(null); setEditedMapping({});
      if (previewType === "sales") toast.success(`${result.imported} ventas importadas${result.detected_format ? ` (${result.detected_format})` : ""}`);
      else if (previewType === "menu") toast.success(`${result.imported_dishes} platos importados`);
      else if (previewType === "expenses") {
        const dupTxt = result.duplicates_skipped ? `, ${result.duplicates_skipped} duplicados omitidos` : "";
        toast.success(`${result.imported} gastos importados${dupTxt}`);
      } else toast.success(`${result.imported} creados, ${result.updated} actualizados`);
      if (result.errors?.length > 0) toast.warning(`${result.errors.length} filas con error`);
    } catch (err) {
      toast.error(err.message || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleCSV = (type) => {
    let content, filename;
    if (type === "sales") {
      content = "date,product_name,quantity,unit_price,total,payment_method\n2026-01-15,Paella Valenciana,2,14.50,29.00,card\n2026-01-15,Cerveza,4,2.50,10.00,card";
      filename = "ventas_ejemplo.csv";
    } else if (type === "menu") {
      content = "name,description,price,category\nPaella Valenciana,Arroz con mariscos,14.50,Arroces\nGazpacho,Sopa fría,6.00,Entrantes";
      filename = "menu_ejemplo.csv";
    } else if (type === "expenses") {
      content = "date,supplier_name,supplier_tax_id,invoice_number,notes,category,base_amount,tax_amount,total_amount,payment_method\n2026-01-15,Makro España,A28000000,F-2026/001,Cash&Carry semanal,purchases,450.00,45.00,495.00,card\n2026-01-22,Endesa,A81948077,ELEC-01-26,Luz enero,utilities,180.00,37.80,217.80,direct_debit\n2026-01-25,Movistar,A82018474,MOV-01-26,Fibra y móvil enero,telecommunications,85.00,17.85,102.85,direct_debit\n2026-01-31,Arrendadora SL,B12345678,ALQ-01-26,Alquiler enero,rent,1500.00,0.00,1500.00,transfer";
      filename = "gastos_ejemplo.csv";
    } else {
      content = "name,quantity,unit,cost_per_unit,min_stock,category\nTomate,10,kg,1.20,2,Verduras\nAceite de oliva,5,l,8.50,1,Aceites";
      filename = "inventario_ejemplo.csv";
    }
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const bulkPollRef = useRef(null);

  const stopBulkPolling = useCallback(() => {
    if (bulkPollRef.current) {
      clearInterval(bulkPollRef.current);
      bulkPollRef.current = null;
    }
  }, []);

  // Cleanup bulk polling on unmount
  useEffect(() => {
    return () => stopBulkPolling();
  }, [stopBulkPolling]);

  const pollBulkJob = (jobId) => {
    stopBulkPolling();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    bulkPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/restaurants/${currentRestaurant.restaurant_id}/tpv/bulk-import-jobs/${jobId}`,
          { credentials: "include" }
        );
        const job = await res.json();
        setBulkResult(job);
        if (job.status === "completed" || job.status === "failed") {
          stopBulkPolling();
          setBulkImporting(false);
          if (job.status === "completed") {
            if (job.imported > 0) {
              toast.success(`${job.imported} ventas importadas correctamente`);
            } else {
              toast.info(`Sin ventas nuevas (${job.skipped || 0} ya existían)`);
            }
          } else {
            toast.error(`Error en la importación: ${job.error || "desconocido"}`);
          }
        }
      } catch {
        // ignore transient poll errors
      }
    }, 2000);
  };

  const handleBulkImport = () => {
    if (!bulkFile) { toast.error("Selecciona un archivo CSV"); return; }
    setBulkImporting(true);
    setBulkResult(null);
    stopBulkPolling();

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const formData = new FormData();
    formData.append("file", bulkFile);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/api/restaurants/${currentRestaurant.restaurant_id}/tpv/bulk-import`);
    xhr.withCredentials = true;

    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON */ }

      if (xhr.status >= 200 && xhr.status < 300) {
        if (data.job_id) {
          // Async job: start polling
          setBulkResult({ status: "queued", total: data.total, progress: 0 });
          toast.info(`Procesando ${data.total.toLocaleString()} filas en segundo plano...`);
          pollBulkJob(data.job_id);
        } else {
          // Immediate result (empty file)
          setBulkImporting(false);
          setBulkResult(data);
          toast.info("Sin ventas que importar");
        }
      } else {
        setBulkImporting(false);
        toast.error(`Error ${xhr.status}: ${data.detail || "Error del servidor"}`);
      }
    };

    xhr.onerror = () => {
      setBulkImporting(false);
      toast.error("Error de red al subir el archivo");
    };

    xhr.ontimeout = () => {
      setBulkImporting(false);
      toast.error("Tiempo de espera agotado al subir el archivo");
    };

    xhr.timeout = 30000;
    xhr.send(formData);
  };

  const filteredSystems = posSystems.filter(s =>
    posSearch ? s.name.toLowerCase().includes(posSearch.toLowerCase()) : true
  );

  const posName = selectedPOS?.id === "other" ? customPOSName : selectedPOS?.name;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tpv-integration-page">
      {/* Header */}
      <PageHeader
        icon={<Plug className="w-5 h-5" />}
        title="Integración TPV"
        description="Conecta tu software TPV con MenuGenius para sincronizar ventas, menú e inventario."
        breadcrumb={[{ label: "Integraciones" }, { label: "TPV" }]}
        actions={
          posConfig?.pos_system_name && (
            <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1.5 text-sm">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
              {posConfig.pos_system_name}
            </Badge>
          )
        }
      />

      {/* KPI Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg"><Key className="w-5 h-5 text-primary" /></div>
            <div><p className="text-2xl font-bold">{status?.active_api_keys || 0}</p><p className="text-xs text-muted-foreground">API Keys activas</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-950/30 rounded-lg"><ShoppingCart className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-2xl font-bold">{status?.tpv_sales_imported || 0}</p><p className="text-xs text-muted-foreground">Ventas importadas</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-950/30 rounded-lg"><UtensilsCrossed className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-2xl font-bold">{status?.tpv_dishes_imported || 0}</p><p className="text-xs text-muted-foreground">Platos importados</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-950/30 rounded-lg">
              {posConfig?.integration_type ? (
                (() => {
                  const M = INTEGRATION_TYPE_META[posConfig.integration_type];
                  return <M.icon className={`w-5 h-5 ${M.color}`} />;
                })()
              ) : <RefreshCw className="w-5 h-5 text-orange-700" />}
            </div>
            <div>
              <p className="text-xs font-medium">Método activo</p>
              <p className="text-sm text-muted-foreground">
                {posConfig?.integration_type ? INTEGRATION_TYPE_META[posConfig.integration_type]?.label : "No configurado"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="configuracion" data-testid="tab-configuracion">
            <Settings className="w-4 h-4 mr-1.5" />
            Mi TPV
          </TabsTrigger>
          <TabsTrigger value="live" data-testid="tab-live" className="relative">
            <Radio className="w-4 h-4 mr-1.5" />
            En Vivo
            {liveFeedData?.today_count > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            )}
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">
            <Upload className="w-4 h-4 mr-1.5" />
            Importar CSV
          </TabsTrigger>
          <TabsTrigger value="apikeys" data-testid="tab-apikeys">
            <Key className="w-4 h-4 mr-1.5" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">
            <Code className="w-4 h-4 mr-1.5" />
            Docs
          </TabsTrigger>
        </TabsList>

        {/* ── CONFIGURACIÓN TPV TAB ── */}
        <TabsContent value="configuracion" className="space-y-6" data-testid="tab-content-configuracion">

          {/* Step 1: POS Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">1</span>
                ¿Qué software TPV usas?
              </CardTitle>
              <CardDescription>
                {countryCode ? `Mostrando TPVs populares en tu región (${countryCode}). ` : ""}
                Selecciona tu software o escribe su nombre.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Buscar TPV (Ej: Square, Agora, Revo...)"
                  value={posSearch}
                  onChange={(e) => setPosSearch(e.target.value)}
                  data-testid="pos-search-input"
                />
              </div>

              {/* POS Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                {filteredSystems.map((pos) => (
                  <button
                    key={pos.id}
                    onClick={() => handleSelectPOS(pos)}
                    data-testid={`pos-option-${pos.id}`}
                    className={`relative flex flex-col items-center p-3 rounded-xl border-2 text-center transition-all hover:border-primary/60 hover:bg-primary/5 cursor-pointer ${
                      selectedPOS?.id === pos.id
                        ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                        : "border-border"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold mb-1.5 ${
                      selectedPOS?.id === pos.id ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                    }`}>
                      {pos.logo_letter}
                    </div>
                    <span className="text-xs font-medium leading-tight">{pos.name}</span>
                    {selectedPOS?.id === pos.id && (
                      <div className="absolute top-1.5 right-1.5">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Custom POS name input (for "other") */}
              {selectedPOS?.id === "other" && (
                <div className="pt-2">
                  <Label htmlFor="custom-pos-name">Nombre de tu TPV</Label>
                  <Input
                    id="custom-pos-name"
                    placeholder="Ej: Mi Software TPV personalizado"
                    value={customPOSName}
                    onChange={(e) => setCustomPOSName(e.target.value)}
                    className="mt-1"
                    data-testid="custom-pos-name-input"
                  />
                </div>
              )}

              {/* Selected indicator */}
              {selectedPOS && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <p className="text-sm">
                    <strong>{posName || selectedPOS.name}</strong> seleccionado
                    {selectedPOS.integration_types?.length > 0 && (
                      <span className="text-muted-foreground"> · Integración disponible: {selectedPOS.integration_types.join(", ")}</span>
                    )}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: AI Recommendation */}
          <Card className={!selectedPOS ? "opacity-60 pointer-events-none" : ""}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">2</span>
                Recomendación de integración con IA
              </CardTitle>
              <CardDescription>
                Gemini analizará tu TPV y recomendará el mejor método de integración
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={handleGetAIRecommendation}
                disabled={!posName || loadingRecommendation}
                className="w-full sm:w-auto"
                data-testid="btn-ai-recommend"
              >
                {loadingRecommendation ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analizando con Gemini...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Analizar {posName || "mi TPV"} con IA</>
                )}
              </Button>

              {aiRecommendation && (
                <div className="space-y-4" data-testid="ai-recommendation-result">
                  {/* Main recommendation */}
                  <div className={`p-4 rounded-xl border-2 ${INTEGRATION_TYPE_META[aiRecommendation.recommended_type]?.border} ${INTEGRATION_TYPE_META[aiRecommendation.recommended_type]?.bg}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const M = INTEGRATION_TYPE_META[aiRecommendation.recommended_type];
                          return <M.icon className={`w-5 h-5 ${M.color}`} />;
                        })()}
                        <div>
                          <p className="font-semibold text-sm">
                            Recomendado: {INTEGRATION_TYPE_META[aiRecommendation.recommended_type]?.label}
                          </p>
                          <p className="text-xs text-muted-foreground">{INTEGRATION_TYPE_META[aiRecommendation.recommended_type]?.desc}</p>
                        </div>
                      </div>
                      <Badge className={CONFIDENCE_LABELS[aiRecommendation.confidence]?.bg + " " + CONFIDENCE_LABELS[aiRecommendation.confidence]?.color + " border-0 text-xs"}>
                        {CONFIDENCE_LABELS[aiRecommendation.confidence]?.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{aiRecommendation.reason}</p>

                    {/* Steps */}
                    {aiRecommendation.steps?.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {aiRecommendation.steps.map((step, i) => (<div key={step.id || step.step_id || `step-${i}`} className="flex items-start gap-2 text-sm">
                            <span className="w-5 h-5 rounded-full bg-card/60 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                            <span>{step.replace(/^Paso \d+: /, "")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Alternative */}
                  {aiRecommendation.alternative && (
                    <div className="flex items-start gap-3 p-3 rounded-lg border border-dashed bg-muted/30">
                      <ArrowRight className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Alternativa: {INTEGRATION_TYPE_META[aiRecommendation.alternative]?.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{aiRecommendation.alternative_reason}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Choose integration method */}
          <Card className={!selectedPOS ? "opacity-60 pointer-events-none" : ""}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">3</span>
                Elige tu método de integración
              </CardTitle>
              <CardDescription>Puedes cambiar tu selección en cualquier momento</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {["webhook", "api", "csv"].map((type) => {
                const M = INTEGRATION_TYPE_META[type];
                const Icon = M.icon;
                const isRecommended = aiRecommendation?.recommended_type === type;
                return (
                  <button
                    key={type}
                    onClick={() => setChosenIntegration(type)}
                    data-testid={`integration-type-${type}`}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all hover:border-primary/60 cursor-pointer ${
                      chosenIntegration === type
                        ? "border-primary bg-primary/5 ring-2 ring-primary/10"
                        : "border-border hover:bg-muted/30"
                    }`}
                  >
                    <div className={`p-2 rounded-lg mt-0.5 ${chosenIntegration === type ? "bg-primary text-white" : "bg-muted"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm">{M.label}</span>
                        {isRecommended && (
                          <Badge className="bg-primary/10 text-primary border-0 text-xs px-1.5 py-0">
                            <Star className="w-2.5 h-2.5 mr-1" />IA recomendado
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{M.desc}</p>
                    </div>
                    {chosenIntegration === type && <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />}
                  </button>
                );
              })}

              <Button
                onClick={handleSaveConfig}
                disabled={!selectedPOS || !chosenIntegration || savingConfig}
                className="w-full mt-2"
                data-testid="btn-save-pos-config"
              >
                {savingConfig ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />Guardar configuración</>
                )}
              </Button>

              {posConfig?.pos_system_name && (
                <p className="text-xs text-center text-muted-foreground">
                  Configuración actual: <strong>{posConfig.pos_system_name}</strong> · {INTEGRATION_TYPE_META[posConfig.integration_type]?.label}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Webhook info card */}
          {(chosenIntegration === "webhook" || posConfig?.integration_type === "webhook") && (
            <WebhookInfoCard restaurantId={currentRestaurant?.restaurant_id} />
          )}
        </TabsContent>

        {/* ── EN VIVO TAB ── */}
        <TabsContent value="live" className="space-y-5" data-testid="tab-content-live">
          {/* Status bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border bg-card">
            <div className="flex items-center gap-3">
              <div className="relative flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${liveIsPolling ? "bg-green-500" : "bg-gray-400"}`}>
                  {liveIsPolling && <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />}
                </span>
                <span className="text-sm font-medium">{liveIsPolling ? "LIVE — Actualizando" : "En pausa"}</span>
              </div>
              {liveFeedData?.last_event_at && (
                <span className="text-xs text-muted-foreground">
                  Último evento: {new Date(liveFeedData.last_event_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {liveIsPolling && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Actualiza en {liveRefreshCountdown}s
                </span>
              )}
              <Button size="sm" variant="outline" onClick={() => fetchLiveFeed(false)} disabled={liveFeedLoading} data-testid="btn-refresh-live">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${liveFeedLoading ? "animate-spin" : ""}`} />
                Actualizar
              </Button>
              <Button size="sm" variant="outline" onClick={handleSendTestEvent} disabled={sendingTestEvent} data-testid="btn-test-event">
                {sendingTestEvent ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                Evento prueba
              </Button>
            </div>
          </div>

          {liveFeedLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                <p className="text-sm text-muted-foreground">Cargando sincronización en vivo...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Today's Stats — based on received_at (not business date) */}
              <div className="grid grid-cols-3 gap-4" data-testid="live-today-stats">
                <Card className="border-green-200 dark:border-green-800">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{liveFeedData?.today_count || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Recibidos hoy</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 dark:border-blue-800">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">
                      {(liveFeedData?.today_revenue || 0).toFixed(2)}€
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Importe recibido hoy</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 dark:border-amber-800">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-700">
                      {(liveFeedData?.today_avg_ticket || 0).toFixed(2)}€
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Ticket medio</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid lg:grid-cols-3 gap-5">
                {/* Live feed */}
                <div className="lg:col-span-2 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Activity className="w-4 h-4 text-primary" />
                      Feed de ventas en tiempo real
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {liveFeedData?.recent_sales?.length || 0} últimas ventas
                    </Badge>
                  </div>

                  {!liveFeedData?.recent_sales?.length ? (
                    <Card className="border-dashed">
                      <CardContent className="py-12 text-center space-y-3">
                        <Radio className="w-10 h-10 mx-auto text-muted-foreground" />
                        <div>
                          <p className="font-medium">Esperando ventas...</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Las ventas aparecerán aquí en tiempo real cuando tu TPV las envíe.
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={handleSendTestEvent} disabled={sendingTestEvent}>
                          {sendingTestEvent ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                          Enviar evento de prueba
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="live-feed-list">
                      {liveFeedData.recent_sales.map((sale) => {
                        const isNew = newSaleIds.has(sale.sale_id);
                        // Is this a historical transaction (business date != today)?
                        const businessDate = sale.created_at ? sale.created_at.slice(0, 10) : null;
                        const todayStr = new Date().toISOString().slice(0, 10);
                        const isHistorical = businessDate && businessDate !== todayStr;
                        const sourceColors = {
                          webhook: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400",
                          webhook_test: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
                          tpv_api: "bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400",
                          csv_import: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
                        };
                        const paymentIcon = {
                          card: <CreditCard className="w-3 h-3" />,
                          cash: <Banknote className="w-3 h-3" />,
                          bizum: <Smartphone className="w-3 h-3" />,
                        };
                        return (
                          <div
                            key={sale.sale_id}
                            data-testid={`live-sale-${sale.sale_id}`}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                              isNew
                                ? "border-green-400 bg-green-50 dark:bg-green-950/20 shadow-sm shadow-green-200"
                                : "border-border bg-card hover:bg-muted/30"
                            }`}
                          >
                            {/* Date / Time */}
                            <div className="text-xs text-muted-foreground w-20 flex-shrink-0 text-center">
                              {isHistorical ? (
                                <>
                                  <div className="font-medium text-amber-700 dark:text-amber-400">
                                    {new Date(sale.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                                  </div>
                                  <div className="text-[10px] opacity-70">
                                    {new Date(sale.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                                  </div>
                                </>
                              ) : (
                                <div>{new Date(sale.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</div>
                              )}
                              {sale.table && sale.table !== "0" && (
                                <div className="text-[10px] text-muted-foreground/70 mt-0.5">Mesa {sale.table}</div>
                              )}
                            </div>
                            {/* Product */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{sale.product_name || "Venta"}</p>
                              <p className="text-xs text-muted-foreground">
                                {sale.quantity > 1 && `×${sale.quantity} · `}
                                {sale.unit_price > 0 && `${sale.unit_price}€/ud`}
                                {sale.ticket_number && (
                                  <span className="ml-1 opacity-60">#{sale.ticket_number}</span>
                                )}
                              </p>
                            </div>
                            {/* Total */}
                            <div className="text-sm font-bold text-right flex-shrink-0">
                              {(sale.total || 0).toFixed(2)}€
                            </div>
                            {/* Payment */}
                            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                              {paymentIcon[sale.payment_method] || <CreditCard className="w-3 h-3" />}
                              {sale.payment_method || "—"}
                            </div>
                            {/* Source badge */}
                            <Badge className={`text-xs px-1.5 py-0 border-0 flex-shrink-0 ${sourceColors[sale.source] || "bg-muted text-muted-foreground"}`}>
                              {sale.source === "webhook_test" ? "test" : sale.source === "csv_import" ? "csv" : sale.source || "—"}
                            </Badge>
                            {isNew && (
                              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Right column: payment breakdown + hourly */}
                <div className="space-y-4">
                  {/* Payment breakdown */}
                  {liveFeedData?.payment_breakdown?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-primary" />
                          Métodos de pago (7 días)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {liveFeedData.payment_breakdown.map((p) => {
                          const total = liveFeedData.payment_breakdown.reduce((s, x) => s + x.count, 0);
                          const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
                          const icons = { card: "💳", cash: "💵", bizum: "📱" };
                          return (
                            <div key={p.method} className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>{icons[p.method] || "💰"} {p.method}</span>
                                <span className="font-medium">{p.count} · {p.total.toFixed(0)}€</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5">
                                <div
                                  className="bg-primary h-1.5 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  )}

                  {/* Hourly distribution */}
                  {liveFeedData?.hourly_distribution?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-primary" />
                          Ventas por hora (hoy)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                      <div className="flex items-end gap-1 h-20 w-full overflow-hidden">
                          {(() => {
                            const maxRev = Math.max(...liveFeedData.hourly_distribution.map(h => h.revenue), 1);
                            return liveFeedData.hourly_distribution.map((h) => (
                              <div key={h.hour} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${h.hour}h: ${h.revenue.toFixed(2)}€`}>
                                <div
                                  className="w-full bg-primary/80 rounded-sm transition-all min-w-[2px]"
                                  style={{ height: `${Math.max((h.revenue / maxRev) * 72, 4)}px` }}
                                />
                                <span className="text-[9px] text-muted-foreground">{h.hour}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Connection info */}
                  <Card className="border-dashed">
                    <CardContent className="p-4 space-y-2">
                      <p className="text-xs font-medium">Estado de conexión</p>
                      <div className="space-y-1.5 text-xs text-muted-foreground">
                        <div className="flex justify-between">
                          <span>Eventos recibidos</span>
                          <span className="font-mono">{liveFeedData?.total_events || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Webhook activo</span>
                          <Badge variant={liveFeedData?.webhook_enabled ? "default" : "secondary"} className="text-[10px] h-4 px-1.5">
                            {liveFeedData?.webhook_enabled ? "Sí" : "No"}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span>Polling activo</span>
                          <Badge variant={liveIsPolling ? "default" : "secondary"} className="text-[10px] h-4 px-1.5">
                            {liveIsPolling ? `Cada 10s` : "No"}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* ── CSV IMPORT TAB ── */}
        <TabsContent value="import" className="space-y-6" data-testid="tab-content-import">
          {/* AI badge */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
            <p className="text-sm">
              <strong>Importación inteligente con IA</strong> — Compatible con Revo XEF, Lightspeed, Square, Agora, ICG, Glop y más. Sube tu CSV o Excel y la IA detectará el formato automáticamente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <CSVImportCard type="sales" fileRef={salesFileRef} analyzing={analyzingCSV && previewType === "sales"} onFileSelect={(f) => handleCSVSelected(f, "sales")} onDownloadSample={() => downloadSampleCSV("sales")} />
            <CSVImportCard type="menu" fileRef={menuFileRef} analyzing={analyzingCSV && previewType === "menu"} onFileSelect={(f) => handleCSVSelected(f, "menu")} onDownloadSample={() => downloadSampleCSV("menu")} />
            <CSVImportCard type="inventory" fileRef={inventoryFileRef} analyzing={analyzingCSV && previewType === "inventory"} onFileSelect={(f) => handleCSVSelected(f, "inventory")} onDownloadSample={() => downloadSampleCSV("inventory")} />
            <CSVImportCard type="expenses" fileRef={expensesFileRef} analyzing={analyzingCSV && previewType === "expenses"} onFileSelect={(f) => handleCSVSelected(f, "expenses")} onDownloadSample={() => downloadSampleCSV("expenses")} />
          </div>

          {/* Reminder section */}
          <Card data-testid="bulk-import-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4 text-primary" />
                Importación masiva histórica
              </CardTitle>
              <CardDescription>
                Carga un CSV completo de tu TPV de golpe. Usa tu API key para autenticar.
                Los registros duplicados se omiten automáticamente (idempotente).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* File picker */}
              <div className="space-y-1.5">
                <Label className="text-sm">Archivo CSV</Label>
                <div
                  onClick={() => bulkFileRef.current?.click()}
                  className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors bg-muted/20 hover:bg-muted/40"
                  data-testid="bulk-file-dropzone"
                >
                  <FileSpreadsheet className="w-8 h-8 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {bulkFile ? (
                      <>
                        <p className="text-sm font-medium text-foreground truncate">{bulkFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(bulkFile.size / 1024).toFixed(1)} KB</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">Haz clic para seleccionar un CSV</p>
                        <p className="text-xs text-muted-foreground">
                          Columnas: numero, mesa, fecha_hora, vendedor, baseimp1, impiva1, total_ticket, descri, cantidad, precio, totlin
                        </p>
                      </>
                    )}
                  </div>
                  {bulkFile && (
                    <button
                      onClick={e => { e.stopPropagation(); setBulkFile(null); setBulkResult(null); if (bulkFileRef.current) bulkFileRef.current.value = ""; }}
                      className="text-muted-foreground hover:text-destructive transition-colors p-1"
                      data-testid="bulk-clear-file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  ref={bulkFileRef}
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={e => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                  data-testid="bulk-file-input"
                />
              </div>

              <Button
                onClick={handleBulkImport}
                disabled={bulkImporting || !bulkFile}
                data-testid="bulk-import-btn"
              >
                {bulkImporting
                  ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Subiendo...</>
                  : <><Upload className="w-3.5 h-3.5 mr-2" />Importar ahora</>
                }
              </Button>

              {/* Progress / Result */}
              {bulkResult && (bulkResult.status === "queued" || bulkResult.status === "processing") && (
                <div className="rounded-xl p-4 border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-sm space-y-3" data-testid="bulk-import-progress">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="font-semibold text-blue-700 dark:text-blue-300">
                      {bulkResult.status === "queued" ? "En cola..." : "Procesando en segundo plano..."}
                    </span>
                  </div>
                  {bulkResult.total > 0 && (
                    <>
                      <div className="w-full bg-blue-200 dark:bg-blue-900 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${bulkResult.progress || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {bulkResult.progress || 0}% — {(bulkResult.processed_rows || 0).toLocaleString()} / {(bulkResult.total || 0).toLocaleString()} filas
                      </p>
                    </>
                  )}
                </div>
              )}

              {bulkResult && (bulkResult.status === "completed" || bulkResult.status === "failed") && (
                <div
                  className={`rounded-xl p-4 border text-sm space-y-1 ${
                    bulkResult.status === "failed"
                      ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                      : bulkResult.imported > 0
                        ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                        : "bg-muted/40 border-border"
                  }`}
                  data-testid="bulk-import-result"
                >
                  {bulkResult.status === "failed" ? (
                    <>
                      <p className="font-semibold text-red-700 dark:text-red-400">Error en la importación</p>
                      <p className="text-xs text-red-700">{bulkResult.error}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">Resultado de la importación</p>
                      <div className="flex flex-wrap gap-4 mt-1">
                        <span className="text-green-700 dark:text-green-400 font-medium">
                          {(bulkResult.imported || 0).toLocaleString()} importadas
                        </span>
                        <span className="text-muted-foreground">
                          {(bulkResult.skipped || 0).toLocaleString()} omitidas (ya existían)
                        </span>
                        <span className="text-muted-foreground">
                          {(bulkResult.total || 0).toLocaleString()} total procesadas
                        </span>
                      </div>
                      {bulkResult.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-700 space-y-0.5">
                          <p className="font-medium">Errores ({bulkResult.errors.length}):</p>
                          {bulkResult.errors.map((e, i) => <p key={`item-${i}`} className="font-mono opacity-80">{e}</p>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reminder section */}
          <Card data-testid="reminder-section">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                Recordatorios de importación
              </CardTitle>
              <CardDescription>
                Recibe emails periódicos para recordarte subir los ficheros de ventas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Activar recordatorios</Label>
                  <p className="text-xs text-muted-foreground">Recibirás un email para recordarte importar los datos</p>
                </div>
                <Switch
                  checked={reminderEnabled}
                  onCheckedChange={setReminderEnabled}
                  data-testid="reminder-toggle"
                />
              </div>

              {reminderEnabled && (
                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="reminder-email" className="text-sm">Email de recordatorio</Label>
                    <Input
                      id="reminder-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={reminderEmail}
                      onChange={(e) => setReminderEmail(e.target.value)}
                      data-testid="reminder-email-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Frecuencia</Label>
                    <Select value={reminderFrequency} onValueChange={setReminderFrequency}>
                      <SelectTrigger data-testid="reminder-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Diariamente</SelectItem>
                        <SelectItem value="weekly">Cada semana</SelectItem>
                        <SelectItem value="monthly">Cada mes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button
                onClick={handleSaveReminder}
                disabled={savingReminder}
                variant={reminderEnabled ? "default" : "outline"}
                size="sm"
                data-testid="btn-save-reminder"
              >
                {savingReminder ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Guardando...</>
                ) : reminderEnabled ? (
                  <><Bell className="w-3.5 h-3.5 mr-2" />Activar recordatorio</>
                ) : (
                  <><X className="w-3.5 h-3.5 mr-2" />Desactivar recordatorio</>
                )}
              </Button>

              {posConfig?.reminder_enabled && (
                <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Recordatorio activo: {posConfig.reminder_email} · {
                    { daily: "diariamente", weekly: "cada semana", monthly: "cada mes" }[posConfig.reminder_frequency]
                  }
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── API KEYS TAB ── */}
        <TabsContent value="apikeys" className="space-y-4" data-testid="tab-content-apikeys">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">API Keys</CardTitle>
                <CardDescription>Crea API keys para que tu TPV envíe datos automáticamente</CardDescription>
              </div>
              <Button onClick={() => { setNewKeyDialogOpen(true); setNewKeyResult(null); setNewKeyName(""); }} data-testid="btn-new-api-key">
                <Plus className="w-4 h-4 mr-2" />Nueva API Key
              </Button>
            </CardHeader>
            <CardContent>
              {apiKeys.length === 0 ? (
                <div className="text-center py-8">
                  <Key className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No hay API keys creadas</p>
                  <p className="text-sm text-muted-foreground">Crea una para conectar tu TPV</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead><TableHead>API Key</TableHead>
                      <TableHead>Permisos</TableHead><TableHead>Último uso</TableHead>
                      <TableHead>Estado</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiKeys.map((key) => (
                      <TableRow key={key.key_id}>
                        <TableCell className="font-medium">{key.name}</TableCell>
                        <TableCell className="font-mono text-xs">{key.api_key}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {key.permissions?.map((p) => (
                              <Badge key={p} variant="outline" className="text-xs">{p.replace("_", " ")}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString("es-ES") : "Nunca"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={key.is_active ? "default" : "secondary"}>
                            {key.is_active ? "Activa" : "Inactiva"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="icon" variant="ghost" onClick={() => handleDeleteKey(key.key_id)} disabled={!key.is_active}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── DOCS TAB ── */}
        <TabsContent value="docs" className="space-y-4" data-testid="tab-content-docs">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Code className="w-5 h-5 text-primary" />
                Documentación de la API
              </CardTitle>
              <CardDescription>Guía para integrar tu sistema TPV con MenuGenius</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Autenticación</h3>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm mb-2">Incluye tu API key en el header de cada petición:</p>
                  <code className="text-sm bg-background p-2 rounded block">X-TPV-API-Key: tpv_xxxxxxxxxxxxxxxxxxxxx</code>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">URL Base</h3>
                <div className="bg-muted p-4 rounded-lg">
                  <code className="text-sm">{status?.api_base_url || window.location.origin + "/api"}</code>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Endpoints disponibles</h3>
                <div className="space-y-3">
                  {[
                    { method: "POST", path: "/tpv/sale/{restaurant_id}", desc: "Importar una venta individual" },
                    { method: "POST", path: "/tpv/sales/{restaurant_id}", desc: "Importar múltiples ventas" },
                    { method: "POST", path: "/tpv/menu/{restaurant_id}", desc: "Importar o actualizar carta/menú" },
                    { method: "POST", path: "/webhooks/{restaurant_id}", desc: "Webhook en tiempo real (requiere secret)" },
                  ].map((ep) => (
                    <div key={ep.path} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge>{ep.method}</Badge>
                        <code className="text-sm">{ep.path}</code>
                      </div>
                      <p className="text-sm text-muted-foreground">{ep.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Códigos de respuesta</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[["200", "Operación exitosa", "green"], ["401", "API key inválida", "red"], ["403", "Sin permisos", "orange"], ["500", "Error del servidor", "red"]].map(([code, desc]) => (
                    <div key={code} className="flex items-center gap-2">
                      <Badge variant="outline">{code}</Badge>
                      <span className="text-muted-foreground">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── New API Key Dialog ── */}      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva API Key</DialogTitle>
            <DialogDescription>Crea una nueva API key para conectar tu TPV</DialogDescription>
          </DialogHeader>
          {newKeyResult ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800 dark:text-green-400">API Key creada</span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-500 mb-3">Guarda esta key ahora. No se volverá a mostrar.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-card dark:bg-zinc-900 rounded border text-sm font-mono break-all">{newKeyResult.api_key}</code>
                  <Button size="icon" variant="outline" onClick={() => copyToClipboard(newKeyResult.api_key)}>
                    {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter><Button onClick={() => setNewKeyDialogOpen(false)}>Cerrar</Button></DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="key-name">Nombre</Label>
                  <Input id="key-name" placeholder="Ej: TPV Principal, Caja 1..." value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
                </div>
                <div>
                  <Label className="mb-3 block">Permisos</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[["sales_write", "Escribir ventas"], ["sales_read", "Leer ventas"], ["menu_write", "Escribir menú"], ["menu_read", "Leer menú"]].map(([key, label]) => (
                      <div key={key} className="flex items-center gap-2">
                        <Checkbox id={`perm-${key}`} checked={newKeyPermissions[key]} onCheckedChange={(v) => setNewKeyPermissions(p => ({ ...p, [key]: v }))} />
                        <Label htmlFor={`perm-${key}`} className="text-sm">{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewKeyDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreateKey} disabled={creatingKey}>
                  {creatingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Crear API Key
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── AI CSV Preview Dialog ── */}
      <Dialog
        open={!!previewData}
        onOpenChange={(open) => { if (!open && !importing) { setPreviewData(null); setPreviewFile(null); setPreviewType(null); setEditedMapping({}); } }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="csv-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Vista previa · Importación IA
            </DialogTitle>
            <DialogDescription>
              La IA ha analizado tu CSV. Revisa el mapeo de columnas y confirma la importación.
            </DialogDescription>
          </DialogHeader>

          {previewData && previewType && (() => {
            const meta = TYPE_META[previewType];
            const fields = FIELD_LABELS[previewType];
            const csvHeaders = previewData.headers || [];
            const sampleRows = previewData.sample_rows || [];
            const mappedCount = Object.entries(fields).filter(([k]) => editedMapping[k]).length;
            return (
              <div className="space-y-4">
                <div className={`flex items-center gap-3 p-3 rounded-lg ${meta.bg}`}>
                  <meta.icon className={`w-5 h-5 ${meta.color}`} />
                  <div>
                    <p className="text-sm font-semibold">{meta.label} · <span className="font-normal">{previewData.detected_format || "Formato personalizado"}</span></p>
                    <p className="text-xs text-muted-foreground">{previewData.total_rows} filas encontradas</p>
                  </div>
                </div>
                {mappedCount === 0 && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg text-sm border border-amber-200">
                    <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-400">La IA no detectó el formato automáticamente</p>
                      <p className="text-amber-700 dark:text-amber-500 text-xs mt-0.5">Selecciona manualmente qué columna del CSV corresponde a cada campo.</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold mb-2">Mapeo de columnas</p>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-xs w-[40%]">Campo estándar</TableHead>
                          <TableHead className="text-xs">Columna del CSV</TableHead>
                          <TableHead className="w-8"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(fields).map(([field, label]) => {
                          const mapped = editedMapping[field];
                          return (
                            <TableRow key={field} className={!mapped ? "opacity-60" : ""}>
                              <TableCell className="text-sm font-medium">{label}</TableCell>
                              <TableCell>
                                <Select
                                  value={mapped || "__none__"}
                                  onValueChange={(val) => setEditedMapping(prev => {
                                    const next = { ...prev };
                                    if (val === "__none__") delete next[field];
                                    else next[field] = val;
                                    return next;
                                  })}
                                >
                                  <SelectTrigger className="h-8 text-xs font-mono" data-testid={`mapping-select-${field}`}>
                                    <SelectValue placeholder="No asignado" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__" className="text-xs italic">— Sin asignar —</SelectItem>
                                    {csvHeaders.map(h => (
                                      <SelectItem key={h} value={h} className="text-xs font-mono">{h}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                {mapped ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <X className="w-3.5 h-3.5 text-muted-foreground" />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                {sampleRows.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold mb-2">Muestra de datos ({sampleRows.length} de {previewData.total_rows} filas)</p>
                    <div className="rounded-lg border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            {Object.entries(fields).filter(([k]) => editedMapping[k]).map(([field, label]) => (
                              <TableHead key={field} className="text-xs whitespace-nowrap">{label}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sampleRows.map((row, i) => (
                            <TableRow key={`row-${i}`}>
                              {Object.entries(fields).filter(([k]) => editedMapping[k]).map(([field]) => {
                                const val = editedMapping[field] ? row[editedMapping[field]] : undefined;
                                return (
                                  <TableCell key={field} className="text-xs font-mono max-w-[120px] truncate">
                                    {val !== null && val !== undefined ? String(val) : <span className="text-muted-foreground">—</span>}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                {previewData.errors?.length > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg text-sm">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{previewData.errors.length} filas con problemas (se omitirán)</span>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreviewData(null); setPreviewFile(null); setPreviewType(null); setEditedMapping({}); }} disabled={importing}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmImport} disabled={importing || Object.keys(editedMapping).length === 0} data-testid="confirm-csv-import-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Importar {previewData?.total_rows || ""} registros
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

